import { useContext, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Tab,
  Tabs,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  ButtonBase,
  Chip,
  Typography,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { styled as muiStyled } from '@mui/material'
import Person from '@mui/icons-material/Person'
import ExitToApp from '@mui/icons-material/ExitToApp'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import Settings from '@mui/icons-material/Settings'
import BarChart from '@mui/icons-material/BarChart'
import EventNote from '@mui/icons-material/EventNote'
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos'
import AssignmentTurnedIn from '@mui/icons-material/AssignmentTurnedIn'
import AccountBalanceWallet from '@mui/icons-material/AccountBalanceWallet'
import Description from '@mui/icons-material/Description'
import HelpOutline from '@mui/icons-material/HelpOutline'
import LocalHospital from '@mui/icons-material/LocalHospital'
import ManageAccounts from '@mui/icons-material/ManageAccounts'
import WorkOutline from '@mui/icons-material/WorkOutline'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import WarningAmber from '@mui/icons-material/WarningAmber'
import { MentorLocalizationContext } from 'src/mentor/context/MentorLocalizationContext'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { getImageLink, logoutHandlerMentor } from 'src/mentor/helpers/utilityFunctions'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { HeaderContainer, HeaderGreeting } from '../MentorHeader/MentorHeaderStyles'
import PortalSwitcherButton from '../MentorHeader/PortalSwitcherButton'
import ClinicOwnerReport from '../ClinicOwnerReport/ClinicOwnerReport'

// Styled Tabs matching mentor portal theme - responsive
const MentorTabs = muiStyled(Tabs, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  backgroundColor: '#f8fafc',
  border: '1px solid rgba(203, 213, 225, 0.82)',
  borderRadius: '8px',
  padding: isMobile ? '3px' : '5px',
  minHeight: 'auto',
  width: '100%',
  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
  overflowX: isMobile ? 'auto' : 'visible',
  display: 'flex',
  visibility: 'visible',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    gap: isMobile ? '2px' : '4px',
    overflowX: isMobile ? 'auto' : 'visible',
    display: 'flex',
    width: '100%',
    justifyContent: isMobile ? 'flex-start' : 'space-between',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    order: 3,
  },
}))

const MentorTab = muiStyled(Tab, {
  shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'iconOnly',
})(({ theme, isMobile, iconOnly }) => ({
    textTransform: 'none',
    minWidth: iconOnly ? '48px' : isMobile ? '120px' : 'auto',
    flex: iconOnly ? '0 0 auto' : isMobile ? '0 1 auto' : '1 1 0',
    maxWidth: iconOnly ? '48px' : 'none',
    fontWeight: theme.typography.fontWeightRegular,
    padding: iconOnly ? '8px' : isMobile ? '8px 12px' : '10px 16px',
    color: '#475569',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontSize: isMobile ? '12px' : '14px',
    fontFamily: "'Inter', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    whiteSpace: 'nowrap',
    display: 'flex !important',
    visibility: 'visible !important',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    '& .MuiTab-iconWrapper': {
      marginRight: iconOnly ? 0 : '8px',
      marginBottom: iconOnly ? 0 : '0px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Only apply icon-only styles when iconOnly is explicitly true
    ...(iconOnly === true && {
      // Hide label text spans, but keep icon wrapper visible
      '& > span:not(.MuiTab-iconWrapper)': {
        display: 'none !important',
      },
      '& .MuiTab-iconWrapper': {
        display: 'flex !important',
        marginRight: '0 !important',
      },
    }),
    '&:hover': {
      color: '#0f766e',
      backgroundColor: '#ecfdf5',
      opacity: 1,
    },
    '&.Mui-selected': {
      color: '#0f766e',
      fontWeight: 700,
      backgroundColor: '#ffffff',
      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
    },
    '&.Mui-focusVisible': {
      backgroundColor: '#ccfbf1',
    },
  }),
)

export default function PreferencesAndSettingsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))
  const localizedData = useContext(MentorLocalizationContext)
  const { mentorProfileData, allLegalPolicies, mentorClinicAssociationData, portalViewSettings } =
    useContext(MentorProfileContext)
  const offersLive = mentorProfileData?.services?.live

  const showPayoutReports = mentorProfileData?.teacherUnEditableFields?.showPayoutReports
  const showEngagementModels = mentorProfileData?.teacherUnEditableFields?.showFeeModels

  const unAcceptedAgreements =
    allLegalPolicies?.filter((agreement) => !agreement?.signed)?.length || 0
  const translatedStrings = localizedData?.localizedData?.strings?.homePage

  const roles = getAllRolesInBoolean()
  const hasClinicAssociations = mentorClinicAssociationData && mentorClinicAssociationData?.length > 0
  const roleChips = [
    roles.isDoctor && 'Doctor',
    roles.isTherapist && 'Therapist',
    roles.isScreener && 'Screener',
    roles.isClinicOwner && 'Clinic owner',
  ].filter(Boolean)

  const settingsSections = [
    {
      title: 'Clinic Workspace',
      cards: [
        hasClinicAssociations && {
          title: translatedStrings?.clinic_bookings || 'EMR Check-in & Bookings',
          eyebrow: 'Clinic operations',
          to: '/mentor-home/my-clinic-associations',
          icon: LocalHospital,
          accent: '#0f766e',
          surface: '#ecfdf5',
          meta: `${mentorClinicAssociationData.length} clinic${
            mentorClinicAssociationData.length === 1 ? '' : 's'
          }`,
          priority: true,
        },
        {
          title: 'Upcoming Appointments',
          eyebrow: 'Schedule',
          to: '/mentor-home/upcoming-appointments',
          icon: EventNote,
          accent: '#2563eb',
          surface: '#eff6ff',
          priority: true,
        },
        offersLive && {
          title:
            translatedStrings?.booked_sessions_and_calendar_settings || 'Calendar Settings',
          eyebrow: 'Availability',
          to: '/mentor-home/my-calendar',
          icon: CalendarMonth,
          accent: '#7c3aed',
          surface: '#f5f3ff',
          meta: 'Live enabled',
          priority: true,
        },
        roles.isClinicOwner && {
          title: 'Stats',
          eyebrow: 'Clinic reporting',
          to: '/mentor-home/stats',
          icon: BarChart,
          accent: '#0891b2',
          surface: '#ecfeff',
        },
      ].filter(Boolean),
    },
    {
      title: 'Clinical Tools',
      cards: [
        roles.isDoctor &&
          mentorProfileData?.allowedToPrescribe && {
            title: translatedStrings?.prescription_templates || 'Prescription Pads',
            eyebrow: 'Templates',
            to: '/mentor-home/prescription-templates',
            icon: Description,
            accent: '#4f46e5',
            surface: '#eef2ff',
          },
        roles.isDoctor && {
          title: translatedStrings?.emr_settings || 'EMR Settings',
          eyebrow: 'Clinical setup',
          to: '/mentor-home/settings',
          icon: Settings,
          accent: '#0f766e',
          surface: '#ecfdf5',
        },
        mentorProfileData?.services?.recorded &&
          mentorProfileData?.contentCategories?.includes('activity-plan') && {
            title: 'Homework Plans',
            eyebrow: 'Patient resources',
            to: '/mentor-home/homework-plans/',
            icon: AssignmentTurnedIn,
            accent: '#ca8a04',
            surface: '#fefce8',
          },
      ].filter(Boolean),
    },
    {
      title: 'Account & Business',
      cards: [
        {
          title: translatedStrings?.my_profile || 'My Profile',
          eyebrow: 'Account',
          to: '/mentor-home/profile',
          icon: ManageAccounts,
          accent: '#0f766e',
          surface: '#ecfdf5',
        },
        {
          title: translatedStrings?.faqs || 'FAQs',
          eyebrow: 'Support',
          to: '/mentor-home/faqs',
          icon: HelpOutline,
          accent: '#475569',
          surface: '#f8fafc',
        },
        allLegalPolicies?.length > 0 && {
          title: translatedStrings?.agreements_tax_and_banking || 'Agreements, Tax and Banking',
          eyebrow: 'Legal',
          to: '/mentor-home/agreements',
          icon: Description,
          accent: '#334155',
          surface: '#f8fafc',
          meta: unAcceptedAgreements > 0 ? `${unAcceptedAgreements} pending` : undefined,
          needsAttention: unAcceptedAgreements > 0,
        },
        roles.isTeacher &&
          !!showPayoutReports && {
            title: 'Your Payout Invoices',
            eyebrow: 'Finance',
            to: '/mentor-home/payout-invoices',
            icon: AccountBalanceWallet,
            accent: '#166534',
            surface: '#f0fdf4',
          },
        roles.isTeacher &&
          !!showPayoutReports && {
            title: 'Your Payout Reports',
            eyebrow: 'Finance',
            to: '/mentor-home/payout-reports',
            icon: BarChart,
            accent: '#166534',
            surface: '#f0fdf4',
          },
        roles.isTeacher &&
          !!showEngagementModels && {
            title: 'Engagement Models',
            eyebrow: 'Business',
            to: '/mentor-home/engagement-models',
            icon: WorkOutline,
            accent: '#a16207',
            surface: '#fefce8',
          },
      ].filter(Boolean),
    },
  ].filter((section) => section.cards.length > 0)

  // Determine active tab based on current route
  const currentTab = location.pathname.includes('preferences-and-settings')
    ? 'preferences'
    : location.pathname.includes('manage-my-bookings')
    ? 'manage-bookings'
    : location.pathname.includes('stats')
    ? 'stats'
    : 'bookings'

  const handleTabChange = (event, newValue) => {
    if (newValue === 'bookings') {
      navigate('/mentor-home/my-clinic-associations')
    } else if (newValue === 'preferences') {
      navigate('/mentor-home/preferences-and-settings')
    } else if (newValue === 'stats') {
      navigate('/mentor-home/stats')
    } else if (newValue === 'manage-bookings') {
      navigate('/mentor-home/manage-my-bookings')
    }
  }

  // Profile dropdown state
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const workspaceStatusItems = [
    hasClinicAssociations && {
      label: 'Clinic access',
      value: `${mentorClinicAssociationData.length} clinic${
        mentorClinicAssociationData.length === 1 ? '' : 's'
      }`,
      icon: LocalHospital,
      accent: '#0f766e',
      surface: '#ecfdf5',
      to: '/mentor-home/my-clinic-associations',
    },
    unAcceptedAgreements > 0 && {
      label: 'Agreements',
      value: `${unAcceptedAgreements} pending`,
      icon: WarningAmber,
      accent: '#b45309',
      surface: '#fffbeb',
      to: '/mentor-home/agreements',
    },
    offersLive && {
      label: 'Calendar',
      value: 'Live enabled',
      icon: CheckCircleOutline,
      accent: '#7c3aed',
      surface: '#f5f3ff',
      to: '/mentor-home/my-calendar',
    },
  ].filter(Boolean)

  const renderSettingsCard = (card, sectionTitle) => {
    const CardIcon = card.icon
    const isWorkspaceCard = sectionTitle === 'Clinic Workspace'
    const isFeatured = isWorkspaceCard && card.priority

    return (
      <ButtonBase
        key={card.to}
        onClick={() => navigate(card.to)}
        sx={{
          width: '100%',
          minHeight: isFeatured ? 154 : 98,
          justifyContent: 'stretch',
          alignItems: 'stretch',
          textAlign: 'left',
          borderRadius: '8px',
          background: isFeatured
            ? `linear-gradient(135deg, #ffffff 0%, ${card.surface} 155%)`
            : '#ffffff',
          border: card.needsAttention
            ? `1px solid ${card.accent}`
            : '1px solid rgba(203, 213, 225, 0.72)',
          boxShadow: isFeatured
            ? '0 18px 38px rgba(15, 23, 42, 0.08)'
            : '0 8px 22px rgba(15, 23, 42, 0.045)',
          overflow: 'hidden',
          transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: card.accent,
            boxShadow: '0 22px 44px rgba(15, 23, 42, 0.12)',
          },
        }}
      >
        {isFeatured ? (
          <Box
            sx={{
              width: '100%',
              p: 2.25,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  backgroundColor: card.accent,
                  boxShadow: `0 14px 28px ${card.accent}30`,
                }}
              >
                <CardIcon sx={{ fontSize: 24 }} />
              </Box>
              {card.meta && (
                <Chip
                  label={card.meta}
                  size="small"
                  sx={{
                    height: 26,
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    color: card.accent,
                    border: `1px solid ${card.accent}24`,
                    fontWeight: 800,
                  }}
                />
              )}
            </Box>
            <Box>
              <Typography
                sx={{
                  color: card.accent,
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                  mb: 0.55,
                }}
              >
                {card.eyebrow}
              </Typography>
              <Typography
                sx={{
                  color: '#0f172a',
                  fontSize: { xs: 18, md: 20 },
                  fontWeight: 850,
                  lineHeight: 1.18,
                }}
              >
                {card.title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ArrowForwardIos sx={{ fontSize: 15, color: card.accent }} />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: '44px minmax(0, 1fr) auto',
              gap: 1.5,
              alignItems: 'center',
              p: 1.75,
              borderLeft: `4px solid ${card.accent}`,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: card.accent,
                backgroundColor: card.surface,
                border: `1px solid ${card.accent}22`,
              }}
            >
              <CardIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: 11,
                  fontWeight: 850,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                  mb: 0.35,
                }}
              >
                {card.eyebrow}
              </Typography>
              <Typography
                sx={{
                  color: '#0f172a',
                  fontSize: 15.5,
                  fontWeight: 800,
                  lineHeight: 1.25,
                }}
              >
                {card.title}
              </Typography>
              {card.meta && (
                <Typography
                  sx={{
                    color: card.needsAttention ? '#b45309' : '#64748b',
                    fontSize: 12,
                    fontWeight: 700,
                    mt: 0.5,
                  }}
                >
                  {card.meta}
                </Typography>
              )}
            </Box>
            <ArrowForwardIos sx={{ fontSize: 14, color: '#94a3b8' }} />
          </Box>
        )}
      </ButtonBase>
    )
  }

  return (
    <>
      {/* Title and Profile/Portal Switcher Row */}
      <Box
        className="hide-on-print-only"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1001,
          backgroundColor: 'rgba(248, 250, 252, 0.94)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'rgba(203, 213, 225, 0.8)',
        }}
      >
        <HeaderContainer className="hide-on-print-only">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              width: '100%',
              py: 1.5,
              gap: 2,
            }}
          >
            {/* Left Column - Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
              <HeaderGreeting
                main
                sx={{
                  fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
                  fontWeight: 600,
                }}
              >
                {currentTab === 'bookings'
                  ? 'Clinic Check-In & Bookings'
                  : currentTab === 'manage-bookings'
                  ? 'Manage Bookings'
                  : currentTab === 'stats'
                  ? 'Stats'
                  : 'Preferences & Settings'}
              </HeaderGreeting>
            </Box>
            {/* Right Column - Buttons (only as wide as needed) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 1 : 1.5,
                flexShrink: 0,
                justifyContent: 'flex-end',
              }}
            >
              <PortalSwitcherButton />
              {isMobile ? (
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    padding: '4px',
                    border: '2px solid rgba(16, 185, 129, 0.2)',
                    backgroundColor: 'rgba(16, 185, 129, 0.04)',
                  }}
                >
                  <Avatar
                    src={getImageLink(mentorProfileData?.profilePicId)}
                    sx={{
                      width: 32,
                      height: 32,
                      border: '2px solid rgba(255, 255, 255, 0.8)',
                    }}
                  >
                    {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </IconButton>
              ) : (
                <Tooltip title="Account Menu" arrow>
                  <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{
                      padding: '6px',
                      border: '2px solid rgba(16, 185, 129, 0.2)',
                      backgroundColor: 'rgba(16, 185, 129, 0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        borderColor: 'rgba(16, 185, 129, 0.4)',
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <Avatar
                      src={getImageLink(mentorProfileData?.profilePicId)}
                      sx={{
                        width: 40,
                        height: 40,
                        border: '2px solid rgba(255, 255, 255, 0.8)',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
                      }}
                    >
                      {mentorProfileData?.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </HeaderContainer>
      </Box>

      {/* Tabs Row - Full Width with Proper Spacing */}
      <Box
        className="hide-on-print-only"
        sx={{
          width: '100%',
          position: 'sticky',
          top: isMobile ? '56px' : '64px',
          zIndex: 1000,
          backgroundColor: 'rgba(248, 250, 252, 0.94)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid',
          borderColor: 'rgba(203, 213, 225, 0.8)',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <MentorTabs value={currentTab} onChange={handleTabChange} isMobile={isMobile}>
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="bookings"
              icon={isMobile ? <CalendarMonth /> : undefined}
              label={isMobile ? 'Check-In' : isTablet ? 'Check-In' : 'Clinic Check-In & Bookings'}
            />
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="manage-bookings"
              icon={isMobile ? <EventNote /> : undefined}
              label={isMobile ? 'Bookings' : isTablet ? 'Bookings' : 'Manage Bookings'}
            />
            {roles.isClinicOwner && (
              <MentorTab
                isMobile={isMobile}
                iconOnly={isMobile}
                value="stats"
                icon={isMobile ? <BarChart /> : undefined}
                label={isMobile ? 'Stats' : 'Stats'}
              />
            )}
            <MentorTab
              isMobile={isMobile}
              iconOnly={isMobile}
              value="preferences"
              icon={isMobile ? <Settings /> : undefined}
              label={isMobile ? 'Settings' : isTablet ? 'Settings' : 'Preferences & Settings'}
            />
          </MentorTabs>
        </Box>
      </Box>

      {/* Profile Dropdown Menu - Outside HeaderContainer (uses portal) */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: 240,
            borderRadius: '12px',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              borderLeft: '1px solid rgba(16, 185, 129, 0.1)',
              borderTop: '1px solid rgba(16, 185, 129, 0.1)',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* User Info Section */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            backgroundColor: 'rgba(16, 185, 129, 0.02)',
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'rgba(15, 23, 42, 0.95)',
              fontSize: '14px',
              mb: 0.5,
            }}
          >
            {mentorProfileData?.name || 'Doctor'}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(100, 116, 139, 0.8)',
              fontSize: '12px',
            }}
          >
            Mentor Portal
          </Typography>
        </Box>

        {/* Profile Menu Item */}
        <MenuItem
          onClick={() => {
            navigate('/mentor-home/profile')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(71, 85, 105, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(16, 185, 129, 0.08)',
              color: 'rgba(16, 185, 129, 0.95)',
            },
          }}
        >
          <Person sx={{ fontSize: 18, mr: 1.5, color: 'rgba(16, 185, 129, 0.7)' }} />
          Profile Settings
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Logout */}
        <MenuItem
          onClick={() => {
            logoutHandlerMentor()
            navigate('/')
            handleClose()
          }}
          sx={{
            py: 1.25,
            px: 2,
            fontSize: '14px',
            color: 'rgba(239, 68, 68, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              color: 'rgba(239, 68, 68, 1)',
            },
          }}
        >
          <ExitToApp sx={{ fontSize: 18, mr: 1.5 }} />
          Sign Out
        </MenuItem>
      </Menu>
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100vh - 128px)',
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 52%)',
          px: { xs: 2, md: 3 },
          py: { xs: 2.5, md: 4 },
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ width: 'min(100%, 1180px)', mx: 'auto' }}>
          <Box
            sx={{
              mb: { xs: 3, md: 3.5 },
              borderRadius: '8px',
              overflow: 'hidden',
              border: '1px solid rgba(203, 213, 225, 0.82)',
              backgroundColor: '#ffffff',
              boxShadow: '0 24px 58px rgba(15, 23, 42, 0.1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  workspaceStatusItems.length > 0
                    ? { xs: '1fr', lg: '1.12fr 0.88fr' }
                    : '1fr',
              }}
            >
              <Box
                sx={{
                  p: { xs: 2.35, md: 3.5 },
                  color: '#ffffff',
                  background:
                    'linear-gradient(135deg, #0f172a 0%, #164e63 58%, #0f766e 100%)',
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: 'rgba(204, 251, 241, 0.92)',
                    fontWeight: 850,
                    letterSpacing: 0,
                    fontSize: 12,
                  }}
                >
                  Mentor Settings
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    mt: 0.35,
                    maxWidth: 620,
                    color: '#ffffff',
                    fontWeight: 850,
                    fontSize: { xs: 28, md: 38 },
                    lineHeight: 1.08,
                    letterSpacing: 0,
                  }}
                >
                  Preferences & Settings
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.75 }}>
                  {roleChips.map((chipLabel) => (
                    <Chip
                      key={chipLabel}
                      label={chipLabel}
                      size="small"
                      sx={{
                        height: 27,
                        borderRadius: '6px',
                        border: '1px solid rgba(204, 251, 241, 0.24)',
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        color: '#ffffff',
                        fontWeight: 800,
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {workspaceStatusItems.length > 0 && (
                <Box
                  sx={{
                    p: { xs: 2.25, md: 2.75 },
                    backgroundColor: '#ffffff',
                    borderLeft: { lg: '1px solid rgba(203, 213, 225, 0.72)' },
                  }}
                >
                  <Typography
                    sx={{
                      color: '#0f172a',
                      fontSize: 17,
                      fontWeight: 850,
                      letterSpacing: 0,
                      mb: 1.5,
                    }}
                  >
                    Active Setup
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    {workspaceStatusItems.map((item) => {
                      const StatusIcon = item.icon

                      return (
                        <ButtonBase
                          key={item.label}
                          onClick={() => navigate(item.to)}
                          sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: '42px minmax(0, 1fr) auto',
                            gap: 1.25,
                            alignItems: 'center',
                            textAlign: 'left',
                            p: 1.25,
                            borderRadius: '8px',
                            border: '1px solid transparent',
                            transition:
                              'background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
                            '&:hover': {
                              backgroundColor: item.surface,
                              borderColor: `${item.accent}24`,
                              transform: 'translateX(2px)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: item.accent,
                              backgroundColor: item.surface,
                              border: `1px solid ${item.accent}20`,
                            }}
                          >
                            <StatusIcon sx={{ fontSize: 21 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#64748b', fontSize: 12, fontWeight: 750 }}>
                              {item.label}
                            </Typography>
                            <Typography
                              sx={{
                                color: '#0f172a',
                                fontSize: 15,
                                fontWeight: 850,
                                lineHeight: 1.25,
                              }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                          <ArrowForwardIos sx={{ fontSize: 13, color: '#94a3b8' }} />
                        </ButtonBase>
                      )
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gap: { xs: 3, md: 3.5 } }}>
            {settingsSections.map((section) => {
              const isWorkspaceSection = section.title === 'Clinic Workspace'

              return (
                <Box key={section.title}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 1.35,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 5,
                          height: 24,
                          borderRadius: '4px',
                          backgroundColor: isWorkspaceSection ? '#0f766e' : '#334155',
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#1e293b',
                          fontWeight: 850,
                          fontSize: 18,
                          letterSpacing: 0,
                        }}
                      >
                        {section.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: isWorkspaceSection
                        ? {
                            xs: '1fr',
                            md: 'repeat(3, minmax(0, 1fr))',
                          }
                        : {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: 'repeat(3, minmax(0, 1fr))',
                          },
                      gap: isWorkspaceSection ? 2 : 1.5,
                    }}
                  >
                    {section.cards.map((card) => renderSettingsCard(card, section.title))}
                  </Box>
                </Box>
              )
            })}
          </Box>

          {roles.isClinicOwner && (
            <Box sx={{ mt: 3, width: '100%' }}>
              <ClinicOwnerReport />
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}
